import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { DEPOSIT_STATE_LABEL, errorMessage, pesos } from "../../lib/rentalLifecycle";

// Task 7: what the renter sees after their rental — what is being claimed,
// what it is based on, what has happened to their security deposit, and how
// to agree or disagree. No internal codes, no staff notes, no accounting
// jargon, and no amount presented as owed while it is only proposed.
//
// Works for a signed-in renter (basePath "/customer/rentals/:id") and for a
// guest holding their booking link ("/public/confirm/:token/rental-settlement").
export default function CustomerSettlementCard({ basePath }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(basePath).then((r) => setData(r.data)).catch(() => setData(null));
  }, [basePath]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const hasSomething = data.charges.length || data.damage_cases.length
    || data.deposit.held_centavos > 0 || data.deposit.applications.length;
  if (!hasSomething) return null;

  const respond = async (url, body, ok) => {
    setBusy(true);
    try { await api.post(url, body); toast.success(ok); load(); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
             data-testid="customer-settlement-card">
      <h2 className="font-semibold">After your rental</h2>

      {data.actions_needed.length > 0 && (
        <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1"
            data-testid="customer-settlement-actions">
          {data.actions_needed.map((a) => <li key={a.key}>{a.text}</li>)}
        </ul>
      )}

      <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid="customer-deposit">
        <h3 className="font-medium">Your security deposit</h3>
        <p>Recorded as received: {pesos(data.summary.security_deposit.received_centavos)}</p>
        <p>Applied to charges: {pesos(data.summary.security_deposit.applied_centavos)}</p>
        <p>Refunded: {pesos(data.summary.security_deposit.refunded_centavos)}</p>
        <p>Still held: {pesos(data.deposit.held_centavos)}</p>
        <p className="mt-1">{DEPOSIT_STATE_LABEL[data.deposit.state] || data.deposit.state} — {data.deposit.why}</p>
      </div>

      {data.charges.length > 0 && (
        <div className="space-y-2" data-testid="customer-charges">
          <h3 className="font-medium text-sm">Charges</h3>
          {data.charges.map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-200 p-3 text-sm"
                 data-testid={`customer-charge-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{c.category_label}</span>
                <span>{c.amount_text}</span>
              </div>
              <p className="text-slate-700">{c.status_text}</p>
              {c.explanation && <p className="mt-1">{c.explanation}</p>}
              {c.evidence.length > 0 && (
                <p className="mt-1 text-xs text-slate-600">
                  {c.evidence.length} file{c.evidence.length === 1 ? "" : "s"} provided as evidence.
                </p>
              )}
              {c.your_response && (
                <p className="mt-1 text-xs">
                  Your response: {c.your_response.response.replace(/_/g, " ").toLowerCase()}
                </p>
              )}
              {c.can_respond && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" disabled={busy} className="text-xs underline"
                          data-testid={`customer-charge-agree-${c.id}`}
                          onClick={() => respond(`${basePath}/charges/${c.id}/respond`,
                                                 { response: "AGREE" }, "Response sent")}>
                    I agree to this amount
                  </button>
                  <button type="button" disabled={busy} className="text-xs underline"
                          onClick={() => respond(`${basePath}/charges/${c.id}/respond`,
                                                 { response: "REQUEST_MORE_INFORMATION" },
                                                 "Request sent")}>
                    Ask for more information
                  </button>
                  <DisputeButton busy={busy} onSubmit={(note) =>
                    respond(`${basePath}/charges/${c.id}/respond`,
                            { response: "DISPUTE", note }, "Your disagreement has been recorded")} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data.damage_cases.length > 0 && (
        <div className="space-y-2" data-testid="customer-damage-cases">
          <h3 className="font-medium text-sm">Damage reported</h3>
          {data.damage_cases.map((c) => (
            <div key={c.id} className="rounded-lg border border-slate-200 p-3 text-sm"
                 data-testid={`customer-damage-${c.id}`}>
              <p className="font-medium">{c.what_was_found}</p>
              {c.where && <p className="text-xs text-slate-600">{c.where}</p>}
              <p className="mt-1">Rental business position: {c.business_position}</p>
              <p className="text-xs text-slate-600">{c.note}</p>
              {!c.your_position && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" disabled={busy} className="text-xs underline"
                          data-testid={`customer-damage-ack-${c.id}`}
                          onClick={() => respond(`${basePath}/damage-cases/${c.id}/position`,
                                                 { position: "ACKNOWLEDGES_DAMAGE" },
                                                 "Recorded")}>
                    I can see this damage
                  </button>
                  <button type="button" disabled={busy} className="text-xs underline"
                          onClick={() => respond(`${basePath}/damage-cases/${c.id}/position`,
                                                 { position: "DISAGREES_DAMAGE_EXISTS" },
                                                 "Recorded")}>
                    I disagree this damage is new
                  </button>
                </div>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Saying you can see the damage is not agreeing to pay for it. Any amount is
                proposed separately and you can agree or disagree with it.
              </p>
            </div>
          ))}
        </div>
      )}

      {data.deposit.applications.length > 0 && (
        <div className="space-y-2" data-testid="customer-deposit-applications">
          <h3 className="font-medium text-sm">Proposed deductions from your deposit</h3>
          {data.deposit.applications.map((a) => (
            <div key={a.id} className="rounded-lg border border-slate-200 p-3 text-sm"
                 data-testid={`customer-application-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>{a.total_text}</span>
                <span className="text-xs">{a.proposed_only ? "Proposed" : "Applied"}</span>
              </div>
              <ul className="mt-1 text-xs text-slate-700">
                {a.allocations.map((al, i) => (
                  <li key={i}>{al.category_label}: {al.amount_text}</li>
                ))}
              </ul>
              {a.explanation && <p className="mt-1">{a.explanation}</p>}
              {a.can_respond && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" disabled={busy} className="text-xs underline"
                          onClick={() => respond(`${basePath}/deposit-applications/${a.id}/respond`,
                                                 { response: "AGREE" }, "Response sent")}>
                    I agree
                  </button>
                  <DisputeButton busy={busy} onSubmit={(note) =>
                    respond(`${basePath}/deposit-applications/${a.id}/respond`,
                            { response: "DISPUTE", note },
                            "Your disagreement has been recorded")} />
                </div>
              )}
              <p className="mt-1 text-xs text-slate-500">{a.recorded_language}</p>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-slate-500" data-testid="customer-settlement-note">
        {data.recorded_language}
      </p>
      <p className="text-xs text-slate-500">{data.summary.not_an_invoice}</p>
    </section>
  );
}

function DisputeButton({ busy, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  if (!open) {
    return (
      <button type="button" className="text-xs underline" onClick={() => setOpen(true)}>
        I disagree
      </button>
    );
  }
  return (
    <form className="w-full space-y-2" onSubmit={(e) => { e.preventDefault(); onSubmit(note); }}>
      <label className="block text-xs" htmlFor="dispute-note">Tell the rental business why</label>
      <textarea id="dispute-note" className="w-full rounded border border-slate-300 p-2 text-sm"
                rows={3} value={note} onChange={(e) => setNote(e.target.value)} required />
      <button type="submit" disabled={busy || !note.trim()}
              className="rounded border border-slate-900 px-3 py-1 text-xs">
        Send
      </button>
    </form>
  );
}
